import React, { lazy, Suspense, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from '../../store'
import { LayoutWrapper, isLoading, LoadingTypes } from '@labrav/common'
import {
  MsftCombinedFormState,
  MsftCombinedFormStatus,
  getMicrosoftCombinedFormData,
} from '../../modules/microsoftCombinedForm'
import { getProducts } from '../../modules/productsListing/action'
import { Product } from '../../modules/productsListing/types'
import { CombinedFormDetailsAzure } from '../../components/ViewCombinedForm'
import { CircularProgress } from '@mui/material'
import { useStyles } from './CombinedFormAzureWrapper.styles'

const CombinedFormAzure = lazy(() =>
  import('../../components/AzureCombinedForm').then(module => ({
    default: module.AzureCombinedForm,
  }))
)

const CombinedFormDetailsAWS = lazy(() =>
  import('../../components/CombinedForm').then(module => ({
    default: module.CombinedFormDetailsAWS,
  }))
)

const DetailsView: React.FC = () => {
  const { cloud } = useParams<{ cloud: string }>()

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {cloud === 'aws' && <CombinedFormDetailsAWS isImpartner={true} />}
      {(cloud === 'azure' || cloud === 'AZURE') && <CombinedFormDetailsAzure />}
    </Suspense>
  )
}

export const CombinedFormAzureWrapper: React.FC = () => {
  const dispatch = useDispatch()
  const queryParams = new URLSearchParams(location.search)
  const labraIsvAccountId = queryParams.get('labra_isv_id') as string
  const labraProductID = queryParams.get('labra_product_id') as string
  const { classes } = useStyles()
  const { labraFormId } = useParams<{
    labraFormId: string
  }>()

  const productsList = useSelector<Product[]>(
    state => state.productsListing.AZURE?.products?.rows || []
  )

  const combinedFormData = useSelector(
    (state: { microsoftCombinedForm: MsftCombinedFormState }) =>
      state.microsoftCombinedForm || ({} as MsftCombinedFormState)
  )
  const loadingProducts = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )

  const loadingform = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.COMBINED_FORM_LOADING)
  )

  useEffect(() => {
    if (labraIsvAccountId) {
      dispatch(getProducts(labraIsvAccountId, 'AZURE'))
    }
  }, [labraIsvAccountId])

  useEffect(() => {
    if (labraFormId && !loadingProducts && productsList.length) {
      const cloudProductId =
        productsList.find(product => product.productId === labraProductID)
          ?.cloudMarketplaceProductId || ''
      dispatch(
        getMicrosoftCombinedFormData({
          formId: labraFormId,
          labraProductId: labraProductID,
          cloudProductId: cloudProductId,
        })
      )
    }
  }, [labraFormId, labraProductID, loadingProducts, productsList])

  if (loadingProducts || loadingform) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={30} />
      </div>
    )
  }

  return (
    <>
      {combinedFormData?.status === MsftCombinedFormStatus.DRAFT ? (
        <LayoutWrapper
          title="Edit Private Offer"
          component={CombinedFormAzure}
          isExternal
          paddingZero
        />
      ) : (
        <LayoutWrapper
          title="Offer Details"
          component={DetailsView}
          isExternal
          paddingZero
        />
      )}
    </>
  )
}

export default CombinedFormAzureWrapper
